import PropTypes from "prop-types";
import { Fragment, useEffect, useMemo, useState } from "react";

// material-ui
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import { Autocomplete, TextField, FormControlLabel, Checkbox } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import RefreshIcon from "@mui/icons-material/Refresh";
import { motion } from "framer-motion";
import { format } from 'date-fns';
import { Link } from "react-router-dom";


// third-party
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

// project-import
import ScrollX from "components/ScrollX";
import MainCard from "components/MainCard";
import { usePageSizeContext } from "contexts/PageSizeContext";
import useAuth from "hooks/useAuth";

import {
  DebouncedInput,
  HeaderSort,
  RowSelection,
  TablePagination,
} from "components/third-party/react-table";

import ExpandingUserDetail from "sections/apps/customer/ExpandingUserDetail";

// assets
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import CircularLoader from "components/CircularLoader";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

export const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta(itemRank);
  return itemRank.passed;
};

// ==============================|| REACT TABLE - LIST ||============================== //

export default function GenericTable({
  data,
  columns,
  loading,
  modalToggler,
  fetchData,
  options,
  variant,
  csvExport,
  additionalControls,
  onRowClick,
  onSortingChange,
  }) {
  const theme = useTheme();
  const downSM = useMediaQuery(theme.breakpoints.down('sm'));
  const { roles = [], isAdmin } = useAuth();
  const inventoryReadOnly = roles.includes('pharmacy_biller') &&
    !(isAdmin || roles.includes('superuser') || roles.includes('pharmacy_store_manager') || roles.includes('account_manager'));
  const inventoryVariants = ['stores', 'inventory', 'bills', 'sale-bills'];
  const inventoryActionDisabled = inventoryReadOnly && inventoryVariants.includes(variant);
  const [sorting, setSorting] = useState([
    { id: columns[0]?.accessorKey || '', desc: true },
  ]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter] = useState(0); // setStatusFilter may be used for filtering data based on status later removing it for now.
	const { setDate, date, searchQuery, setSearchQuery, searchPlaceholder, totalRecords, supplierList, supplier, setSupplier, onPageChange, onPageSizeChange, initialPage, initialPageSize, dateFrom, setDateFrom, dateTo, setDateTo, stores, selectedToStore, setSelectedToStore, showActiveOnly, setShowActiveOnly, groupOptions, selectedGroups, setSelectedGroups, subgroupOptions, selectedSubgroups, setSelectedSubgroups } = options || {};
	const [pageIndex, setPageIndex] = useState(initialPage || 0);
  const { pageSize: preferredPageSize, setPageSize: setPreferredPageSize, hasStoredValue } = usePageSizeContext();
  // When options.initialPageSize is set (e.g. server-paginated tables), use it so page count and Next button are correct
  const resolvedInitialPageSize = (initialPageSize != null && initialPageSize !== undefined)
    ? initialPageSize
    : (hasStoredValue ? preferredPageSize : preferredPageSize || 100);
  const [pageSize, setPageSize] = useState(resolvedInitialPageSize);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [hoveredPatientGroup, setHoveredPatientGroup] = useState(null);

  useEffect(() => {
    const nextSize = (initialPageSize != null && initialPageSize !== undefined)
      ? initialPageSize
      : (hasStoredValue ? preferredPageSize : preferredPageSize || 100);
    if (nextSize !== pageSize) {
      setPageSize(nextSize);
      if (onPageSizeChange) {
        onPageSizeChange(nextSize);
      }
    }
  }, [preferredPageSize, hasStoredValue, initialPageSize, pageSize, onPageSizeChange]);

  // Sync pageIndex when parent resets initialPage (e.g. on filter changes)
  useEffect(() => {
    if (initialPage != null && initialPage !== pageIndex) {
      setPageIndex(initialPage);
    }
  }, [initialPage]);

  useEffect(() => {
    if (data.length > pageSize) {
      setAllData(data);
      const start = pageIndex * pageSize;
      const end = start + pageSize;
      setFilteredData(data.slice(start, end));
    } else {
      setAllData(data);
      setFilteredData(data);
    }
  }, [data, pageSize, pageIndex]);

  useEffect(() => {
    setHoveredPatientGroup(null);
  }, [data]);

  const handlePageChange = (newPageIndex) => {
    const updatedOffset = newPageIndex * pageSize;
    if (allData.length > pageSize) {
      setFilteredData(allData.slice(updatedOffset, updatedOffset + pageSize));
    } else {
      fetchData(`limit=${pageSize}&offset=${updatedOffset}`);
    }
    setPageIndex(newPageIndex);
    if (onPageChange) {
      onPageChange(newPageIndex);
    }
  };

  const handleRowsPerPageChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPreferredPageSize(newPageSize);
    setPageIndex(0);
    if (allData.length > newPageSize) {
      setFilteredData(allData.slice(0, newPageSize));
    } else {
      fetchData(`limit=${newPageSize}&offset=0`);
    }
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  const handleExportCSV = async () => {
    const isEnhancedExport = typeof csvExport === 'object';
    let exportHeaders = [];
    let exportData = filteredData;
    let filename = '';
    
    const generateFilename = (baseFilename) => {
      const dateStr = date ? format(date, 'dd-MM-yyyy') : format(new Date(), 'dd-MM-yyyy');
      return baseFilename + dateStr + '.csv';
    };
    
    if (isEnhancedExport) {
      const exportColumns = csvExport.columns || columns;
      
      exportHeaders = exportColumns.map(col => ({
        label: typeof col.header === 'string' ? col.header : '#',
        key: col.accessorKey,
        meta: col.meta
      }));
      
      if (csvExport.getData && typeof csvExport.getData === 'function') {
        exportData = await csvExport.getData();
      } else {
        exportData = csvExport.data || filteredData;
      }
      
      filename = generateFilename(csvExport.filename);
    } else {
      exportHeaders = headers;
      exportData = filteredData;
      filename = generateFilename(csvExport);
    }
    
    const headerRow = exportHeaders.map(header => `"${header.label}"`).join(',');
    
    const dataRows = exportData.map(row => {
      return exportHeaders.map(header => {
        let cell = row[header.key] || "";
        if (header.meta && header.meta.csvFormatter) {
          cell = header.meta.csvFormatter(cell, row);
        }
        return `"${cell}"`;
      }).join(',');
    });
    
    const csvContent = [headerRow, ...dataRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  

  const AddTitle = {
    appointment: <RefreshIcon />,
    opd: "Add Appointment",
    medicine: "Add Medicine",
    nonmedicine: "Add Non-Medicine",
    labtest:"Add Test",
    labpackage: "Add Package",
    users: "Add User",
    stores: "Add Store"
  };

  const filteredDataWithPagination = useMemo(() => {
    if (!statusFilter) return filteredData;
    return filteredData.filter((customer) => customer.status === statusFilter);
  }, [statusFilter, filteredData]);

  // sorting is handled inline in useReactTable's onSortingChange

  const table = useReactTable({
    data: filteredDataWithPagination,
    columns,
		state: {
			sorting,
			rowSelection,
			globalFilter,
		},
    enableRowSelection: true,
    onSortingChange: (updaterOrValue) => {
      setSorting((oldSorting) => {
        const nextSorting = typeof updaterOrValue === 'function'
          ? updaterOrValue(oldSorting)
          : updaterOrValue;

        if (onSortingChange) {
          onSortingChange(nextSorting);
        }

        return nextSorting;
      });
    },
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
		getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
		getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: fuzzyFilter,
    debugTable: true,
    manualPagination: false,
    autoResetPageIndex: false,
  });

  const backColor = alpha(theme.palette.primary.lighter, 0.1);

  // Freeze-pane (toolbar-only) for the Pathology Management list page.
  // The dashboard's AppBar is `position: fixed` at ~64px; we stick the
  // toolbar (search + date + filters) right below it so it stays visible
  // during scroll. We deliberately do NOT `position: sticky` the column-
  // header row too — cell-level sticky on `<th>` in a table with the MUI
  // default `border-collapse: collapse` breaks the column alignment (the
  // last column detaches from the header row and the empty-state colspan
  // renders above the header). Sticky column headers would require moving
  // the whole page to a scoped scroll container, which is a bigger change.
  const isStickyVariant = variant === 'lab-management';
  const APP_BAR_STICKY_OFFSET = 64;

  const stickyToolbarSx = isStickyVariant
    ? {
        position: 'sticky',
        top: APP_BAR_STICKY_OFFSET,
        zIndex: 4,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`
      }
    : undefined;

  let headers = [];
  columns.map(
    (columns) =>
      // @ts-ignore
      columns.accessorKey &&
      headers.push({
        label: typeof columns.header === "string" ? columns.header : "#",
        // @ts-ignore
        key: columns.accessorKey,
        meta: columns.meta,
      })
  );

  return (
    <MainCard content={false} sx={isStickyVariant ? { overflow: 'visible' } : undefined}>
      <Box sx={stickyToolbarSx}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          p: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 640px',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            minWidth: 0,
            ...(downSM && {
              flexBasis: '100%',
              '& > .MuiFormControl-root, & > .MuiAutocomplete-root, & > .MuiStack-root': { width: '100%' }
            })
          }}
        >
          {/* Render search only when a setter is provided to avoid calling undefined handlers */}
          {typeof setSearchQuery === 'function' && (
            <DebouncedInput
              value={searchQuery ?? ""}
              onFilterChange={(value) => setSearchQuery(String(value))}
              placeholder={searchPlaceholder || `Search ${data.length} records...`}
              size="small"
            />
          )}

          {options?.additionalFilters}

          {(variant === "medicine" || variant === "nonmedicine" || variant === "labtest" || variant === "labpackage") && (
            <>
              {variant === "nonmedicine" && (
                <Autocomplete
                  value={options?.category || null}
                  options={options?.categoryList || []}
                  onChange={(_, newValue) => {
                    options?.setCategory(newValue);
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by category"
                      sx={{ minWidth: 300 }}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          type: "search"
                        },
                      }}
                    />
                  )}
                />
              )}
              
              {/* Existing supplier filter for medicine, nonmedicine and inventory */}
              {(variant === "medicine" || variant === "nonmedicine" || variant === "inventory") && (
                <Autocomplete
                  value={supplier || null}
                  options={supplierList || []}
                  onChange={(_, newValue) => {
                    setSupplier(newValue);
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by supplier"
                      sx={{ minWidth: 300 }}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          type: "search"
                        },
                      }}
                    />
                  )}
                />
              )}

              {/* Show Active Only Checkbox */}
              {setShowActiveOnly && showActiveOnly !== undefined && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showActiveOnly}
                      onChange={(e) => setShowActiveOnly(e.target.checked)}
                    />
                  }
                  label="Show Active Only"
                  sx={{ ml: 1 }}
                />
              )}

              {/* Group multiselect filter for labtest */}
              {variant === "labtest" && groupOptions && groupOptions.length > 0 && (
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 }, maxWidth: { sm: 360 } }}
                  value={selectedGroups || []}
                  options={groupOptions}
                  onChange={(_, newValue) => {
                    setSelectedGroups(newValue);
                    setPageIndex(0);
                    if (onPageChange) onPageChange(0);
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Group"
                      placeholder="Select groups..."
                    />
                  )}
                  limitTags={2}
                  disableCloseOnSelect
                />
              )}

              {/* SubGroup multiselect filter for labtest */}
              {variant === "labtest" && subgroupOptions && subgroupOptions.length > 0 && (
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 }, maxWidth: { sm: 360 } }}
                  value={selectedSubgroups || []}
                  options={subgroupOptions}
                  onChange={(_, newValue) => {
                    setSelectedSubgroups(newValue);
                    setPageIndex(0);
                    if (onPageChange) onPageChange(0);
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Subgroup"
                      placeholder="Select subgroups..."
                    />
                  )}
                  limitTags={2}
                  disableCloseOnSelect
                />
              )}

              {/* <Autocomplete
                // value={supplier || null}
                options={substance || []}
                onInputChange={(e, value) => setSubstanceSearch(value)}
                onChange={(_, newValue) => {
                  
                }}
                getOptionLabel={(option) => option?.substance_name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Substance"
                    sx={{ minWidth: 300 }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        type: "search"
                      },
                    }}
                  />
                )}
              /> */}
            </>
          )}
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems='center'
          sx={{
            flex: '0 1 auto',
            flexWrap: 'wrap',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' },
            ml: { sm: 'auto' },
            '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } }
          }}
        >
          {(variant === "appointment" || variant === "opd" || variant === "radiology-management" || variant === "lab-management" || variant === "sale-bills") && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              {variant === "appointment" && (setDateFrom || setDateTo) ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  {stores && setSelectedToStore && (
                    <Autocomplete
                      options={[{ id: '', name: 'All Stores' }, ...stores]}
                      getOptionLabel={(option) => option.name || ''}
                      value={[{ id: '', name: 'All Stores' }, ...stores].find(store => store.id === selectedToStore) || { id: '', name: 'All Stores' }}
                      onChange={(event, newValue) => {
                        setSelectedToStore(newValue ? newValue.id : '');
                      }}
                      sx={{ minWidth: 200 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To Store"
                          size="medium"
                          placeholder="Select store..."
                        />
                      )}
                    />
                  )}
                  <DatePicker
                    format='dd/MM/yyyy'
                    label="From Date"
                    value={dateFrom}
                    onChange={(newValue) => {
                      if (setDateFrom) {
                        setDateFrom(newValue);
                        if (dateTo && newValue && newValue > dateTo && setDateTo) {
                          setDateTo(null);
                        }
                      }
                    }}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        size: "medium",
                        sx: { minWidth: 200 }
                      }
                    }}
                  />
                  <DatePicker
                    format='dd/MM/yyyy'
                    label="To Date"
                    value={dateTo}
                    onChange={(newValue) => setDateTo && setDateTo(newValue)}
                    minDate={dateFrom || undefined}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        size: "medium",
                        sx: { minWidth: 200 }
                      }
                    }}
                  />
                </Stack>
              ) : (
                <DatePicker
                  format='dd/MM/yyyy'
                  label={"Select Date"}
                  value={date ?? null}
                  onChange={(newValue) => setDate(newValue)}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () => setDate(null)
                    },
                    textField: {
                      placeholder: 'All dates'
                    }
                  }}
                />
              )}
            </LocalizationProvider>
          )}
          {variant === 'bills' ? 
            <Stack direction='row' spacing={2} alignItems='center'>
              <Button
                variant='outlined'
                startIcon={<PlusOutlined />}
                onClick={inventoryReadOnly ? undefined : modalToggler}
                disabled={inventoryReadOnly}
              >
                Create Supplier
              </Button>
              <Button
                variant='contained'
                onClick={() => {}}
                startIcon={<ReceiptLongOutlinedIcon />}
                disabled={inventoryReadOnly}
                component={inventoryReadOnly ? undefined : Link}
                to={inventoryReadOnly ? undefined : "/inventory/bills/add-bill"}
                sx={inventoryReadOnly ? { opacity: 0.5 } : undefined}
              >
                Add Bill
              </Button>
            </Stack>
          : null}
          {variant === 'sale-bills' ? 
            <Stack direction='row' spacing={2} alignItems='center'>
              <Button
                variant='contained'
                onClick={() => {}}
                startIcon={<ReceiptLongOutlinedIcon />}
                disabled={inventoryReadOnly}
                component={inventoryReadOnly ? undefined : Link}
                to={inventoryReadOnly ? undefined : "/inventory/sale-bills/add-bill"}
                sx={inventoryReadOnly ? { opacity: 0.5 } : undefined}
              >
                Create Sale Bill
              </Button>
            </Stack>
          : null}
          {variant === 'inventory' ? 
            <Stack direction='row' spacing={2} alignItems='center'>
              <Button
                variant='contained'
                onClick={() => {}}
                startIcon={<ReceiptLongOutlinedIcon />}
                disabled={inventoryReadOnly}
                component={inventoryReadOnly ? undefined : Link}
                to={inventoryReadOnly ? undefined : "/inventory/bills/add-bill"}
                sx={inventoryReadOnly ? { opacity: 0.5 } : undefined}
              >
                Add Bill
              </Button>
            </Stack>
          : null}
            {!csvExport ? 
            <Stack direction='row' spacing={2} alignItems='center'>
              {additionalControls && variant === 'users' ? (
                <Box sx={{ mr: 1 }}>
                  {additionalControls}
                </Box>
              ) : null}
              {AddTitle[variant] && modalToggler && <Button
                variant='contained'
                startIcon={
                  !(variant === "appointment") ? (
                    <PlusOutlined />
                  ) : null
                }
                onClick={inventoryActionDisabled ? undefined : modalToggler}
                disabled={inventoryActionDisabled}
              >
                {AddTitle[variant]}
              </Button>}
              {additionalControls && variant !== 'users' ? additionalControls : null}
            </Stack>
          : null}
          {csvExport ? 
            <Stack direction='row' spacing={2} alignItems='center'>
              <Button
                variant='contained'
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </Stack>
          : null}
        </Stack>
      </Box>
      </motion.div>
      </Box>
      <ScrollX>
        <Stack>
          <RowSelection selected={Object.keys(rowSelection).length} />
          <TableContainer sx={{ minHeight:'650px' }}>
            <motion.div
              key={pageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: loading ? 0.5 : 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Table size="small">
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      if (
                        header.column.columnDef.meta !== undefined &&
                        header.column.getCanSort()
                      ) {
                        Object.assign(header.column.columnDef.meta, {
                          className:
                            header.column.columnDef.meta.className +
                            " cursor-pointer prevent-select",
                        });
                      }

                      return (
                        <TableCell
                          key={header.id}
                          {...header.column.columnDef.meta}
                          onClick={header.column.getToggleSortingHandler()}
                          {...(header.column.getCanSort() &&
                            header.column.columnDef.meta === undefined && {
                              className: "cursor-pointer prevent-select",
                            })}
                        >
                          {header.isPlaceholder ? null : (
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              <Box>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                              </Box>
                              {header.column.getCanSort() ? (
                                <HeaderSort column={header.column} />
                              ) : null}
                          </Stack>
                          )}
                        </TableCell>
                      );
                    })}
                    </TableRow>
                  ))}
                </TableHead>
                {loading && data.length === 0 ? (
                  <TableBody>
                    <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      sx={{ textAlign: "center" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                          padding: "10px",
                          position: "relative",
                        }}
                      >
                        <Stack
                          justifyContent='center'
                          alignItems='center'
                          sx={{ width: "100%", height: "100%" }}
                        >
                          <CircularLoader />
                          <p>Please wait, your data is loading...</p>
                        </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : data.length > 0 ? (
                  <TableBody>
                    {table.getRowModel().rows.map((row) => {
                      const isLabGroupHovered =
                        variant === 'lab-management' &&
                        row.original._patientGroupKey &&
                        row.original._patientGroupKey === hoveredPatientGroup;
                      const isNonOpdCareSetting =
                        ['lab-management', 'radiology-management'].includes(variant) &&
                        row.original?.ipd_location?.is_admitted === true;

                      return (
                      <Fragment key={row.id}>
                        <TableRow
                          hover={variant !== 'lab-management' || Boolean(onRowClick)}
                          data-lab-report-id={
                            variant === 'lab-management' && row.original?.id != null
                              ? String(row.original.id)
                              : undefined
                          }
                          data-patient-group={
                            variant === 'lab-management'
                              ? row.original._patientGroupKey
                              : undefined
                          }
                          sx={{
                            cursor: onRowClick ? 'pointer' : 'default',
                            ...(isLabGroupHovered ? { bgcolor: 'action.hover' } : {}),
                            ...(isNonOpdCareSetting
                              ? {
                                  // `!important` so the tint wins over the
                                  // theme's zebra-stripe/hover rules in
                                  // overrides/TableBody.js, which are more
                                  // specific than this row-level class.
                                  bgcolor: 'rgba(244, 67, 54, 0.08) !important',
                                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.12) !important' }
                                }
                              : {})
                          }}
                          onMouseEnter={() => {
                            if (
                              variant === 'lab-management' &&
                              row.original._patientGroupKey
                            ) {
                              setHoveredPatientGroup(row.original._patientGroupKey);
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (variant !== 'lab-management') return;
                            const groupKey = row.original._patientGroupKey;
                            if (!groupKey) return;
                            const related = e.relatedTarget;
                            if (
                              related?.closest?.(
                                `[data-patient-group="${groupKey}"]`
                              )
                            ) {
                              return;
                            }
                            setHoveredPatientGroup(null);
                          }}
                          onClick={() => onRowClick?.(row.original)}
                        >
                          {row.getVisibleCells().map((cell) => {
                            const meta = cell.column.columnDef.meta || {};
                            const { rowSpanPatient, sx: metaSx, ...cellMeta } = meta;
                            const usePatientRowSpan =
                              variant === 'lab-management' && rowSpanPatient;
                            if (usePatientRowSpan && row.original._showPatientCell === false) {
                              return null;
                            }
                            const patientRowSpan =
                              usePatientRowSpan && row.original._showPatientCell
                                ? row.original._patientRowSpan || 1
                                : undefined;
                            return (
                              <TableCell
                                key={cell.id}
                                rowSpan={patientRowSpan}
                                {...cellMeta}
                                sx={{
                                  py: 0.25,
                                  px: 1,
                                  ...(variant === 'lab-management'
                                    ? {
                                        verticalAlign: 'top',
                                        // Thick, dark divider between patient
                                        // rows in Pathology Management so
                                        // entries are visually separated at a
                                        // glance. `!important` needed because
                                        // the theme's `MuiTableCell.root`
                                        // override sets a lighter divider
                                        // colour and can win due to CSS
                                        // ordering.
                                        borderBottom: '2px solid #333 !important',
                                        borderTop: '0 !important'
                                      }
                                    : patientRowSpan
                                      ? { verticalAlign: 'middle' }
                                      : {}),
                                  ...(usePatientRowSpan && isLabGroupHovered
                                    ? { bgcolor: 'action.hover' }
                                    : {}),
                                  ...(metaSx || {})
                                }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      {row.getIsExpanded() ? (
                          <TableRow
                          sx={{
                            bgcolor: backColor,
                            "&:hover": { bgcolor: `${backColor} !important` },
                          }}
                          >
                            <TableCell colSpan={row.getVisibleCells().length}>
                              <ExpandingUserDetail data={row.original} />
                            </TableCell>
                          </TableRow>
                      ) : null}
                      </Fragment>
                    );
                    })}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      sx={{ textAlign: "center" }}
                    >
                      <Box
												sx={{
													display: "flex",
													justifyContent: "center",
													width: "full",
													padding: "10px",
												}}
											>
												<Stack justifyContent='center' alignItems='center'>
                          <p>No data found</p>
												</Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </motion.div>
          </TableContainer>
          <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <TablePagination
              getPageCount={()=> Math.ceil((totalRecords ?? filteredData.length) / pageSize)}
              totalRecords={totalRecords ?? (filteredData.length > 0 ? filteredData.length : undefined)}
              setPageIndex={handlePageChange}
              setPageSize={handleRowsPerPageChange}
              getState={() => ({ pagination: { pageIndex, pageSize } })}
              initialPageSize={pageSize}
              visibleRows={table.getRowModel().rows.length}
            />
          </Box>
          </>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

GenericTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  options: PropTypes.object,
  loading: PropTypes.bool,
  modalToggler: PropTypes.func,
  fetchData: PropTypes.func,
  variant: PropTypes.string,
  csvExport: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      filename: PropTypes.string,
      columns: PropTypes.array,
      getData: PropTypes.func,
      data: PropTypes.array
    })
  ]),
  additionalControls: PropTypes.node,
  onRowClick: PropTypes.func,
  onSortingChange: PropTypes.func,
};
